import { graphql } from '~/resources/graphql'
import { createError, useCookie } from '#app'
import { useQueryClient } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import type {
    Language,
    OrganizationLanguageContextQueryQuery
} from '~/resources/graphql/graphql'
import { useI18n } from 'vue-i18n'
import { computed, inject, provide, ref } from 'vue'
import type { InjectionKey, Ref } from 'vue'
import { useHead } from '@unhead/vue'

export type SupportedLocale = 'en-US' | 'es-MX' | 'fr-FR' | 'de-DE'
type DocumentLanguage = 'en' | 'es-MX' | 'fr' | 'de'
// Reuse the existing cookie so returning visitors keep their prior locale while
// fresh visitors without a preference receive the organization default.
export const LOCALE_COOKIE_NAME = 'cpx_locale'
const organizationDefaultLocaleInjectionKey: InjectionKey<
    Ref<SupportedLocale>
> = Symbol('organizationDefaultLocaleInjectionKey')

export function useOrganizationDefaultLocale(): Ref<SupportedLocale> {
    const organizationDefaultLocale = inject(
        organizationDefaultLocaleInjectionKey
    )

    if (organizationDefaultLocale === undefined) {
        throw new Error('No Organization Default Locale')
    }

    return organizationDefaultLocale
}

export function mapLanguageToLocale(language: Language): SupportedLocale {
    switch (language) {
        case 'ENGLISH':
            return 'en-US'
        case 'SPANISH_MX':
            return 'es-MX'
        case 'FRENCH':
            return 'fr-FR'
        case 'GERMAN':
            return 'de-DE'
    }
}

function mapLocaleToDocumentLanguage(
    locale: SupportedLocale | string
): DocumentLanguage {
    switch (locale) {
        case 'en-US':
            return 'en'
        case 'es-MX':
            return 'es-MX'
        case 'fr-FR':
            return 'fr'
        case 'de-DE':
            return 'de'
        default:
            return 'en'
    }
}

export async function createLanguageContext() {
    const { locale } = useI18n({ useScope: 'global' })
    const query = useRpQuery({ orgLevel: true })
    const queryClient = useQueryClient()
    const organizationDefaultLocale = ref<SupportedLocale>('en-US')
    const rememberedLocale = useCookie<SupportedLocale | undefined>(
        LOCALE_COOKIE_NAME,
        {
            path: '/',
            sameSite: 'lax'
        }
    )

    // Agreement flows need the org default when releasing a temporary language override,
    // so provide a ref now and fill it with the fetched organization value below.
    provide(organizationDefaultLocaleInjectionKey, organizationDefaultLocale)

    const result = await queryClient.fetchQuery({
        queryKey: ['OrganizationLanguageContextQuery'],
        queryFn: (): Promise<OrganizationLanguageContextQueryQuery> => {
            return query(
                graphql(/* GraphQL */ `
                    query OrganizationLanguageContextQuery {
                        organization {
                            enabledLanguages
                            defaultStorefrontLanguage
                        }
                    }
                `)
            )
        }
    })

    if (result.organization === undefined) {
        throw createError({
            statusCode: 404,
            // Language context must exist before any storefront copy is fetched
            // because later organization queries need the resolved public language.
            statusMessage: 'Page Not Found'
        })
    }

    const defaultLocale = mapLanguageToLocale(
        result.organization.defaultStorefrontLanguage
    )
    organizationDefaultLocale.value = defaultLocale

    // Always allow the configured default even if enabledLanguages is misconfigured.
    const supportedLocales = Array.from(
        new Set([
            ...result.organization.enabledLanguages.map(mapLanguageToLocale),
            defaultLocale
        ])
    )

    const rememberedSupportedLocale =
        rememberedLocale.value !== undefined &&
        supportedLocales.includes(rememberedLocale.value)
            ? rememberedLocale.value
            : undefined
    const nextLocale = rememberedSupportedLocale ?? defaultLocale

    if (
        rememberedLocale.value !== undefined &&
        !supportedLocales.includes(rememberedLocale.value)
    ) {
        // A saved language that the org no longer offers must not override the
        // organization default on later requests.
        rememberedLocale.value = undefined
    }

    if (locale.value !== nextLocale) {
        // Set the i18n locale before createOrganizationContext chooses
        // language-dependent storefront copy.
        locale.value = nextLocale
    }

    const documentLanguage = computed(() => {
        return mapLocaleToDocumentLanguage(locale.value)
    })

    useHead({
        htmlAttrs: {
            // Keep the root document language aligned with the validated CPX language.
            lang: documentLanguage
        }
    })
}
